import React, { useEffect, useState } from 'react';
import packageJSON from '../package.json';
import CacheBuster from 'react-cache-buster';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { useFetchCurrentUserLazyQuery } from '@/graphql/generated/graphql';
import defineUserQueryData from '@/utils/defineUserQueryData';
import { Loader } from '@/components';
import { currentUserInitialState, setCurrentUser, setUserEmployeeData } from '@/graphql/operations/user';
import { getAccessToken } from '@/utils/accessToken';
import Routes from './pages';
import LocalStorage from '@/service/LocalStorage';
import ApiService from '@/service/Api';
import { loginlessPages } from '@/utils/getLoginlessPages';
import { useInactivityLogout } from '@/utils/hooks/useInactivityLogout';
import { validateToken } from '@/utils/tokenValidation';

const App = () => {
  const isProduction = process.env.NODE_ENV === 'production';
  const location = useLocation();
  const client = useApolloClient();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  useInactivityLogout();

  const [fetchCurrentUser, { data: currentUserQuery, error: currentUserQueryError, loading: fetchCurrentUserLoading }] =
    useFetchCurrentUserLazyQuery();
  const [{ data: employeeQuery, error: employeeQueryError, loading: employeeQueryLoading }, fetchEmployee] =
    ApiService.useFetchCurrentEmployee({
      manual: true,
    });

  useEffect(() => {
    async function getCurrentAuthenticatedUserContext() {
      const authToken = getAccessToken({ withLocalStorage: true });
      const isAuthTokenValid = await validateToken(authToken, false);
      if (isAuthTokenValid) {
        await fetchCurrentUser();
        await fetchEmployee();
      }
    }

    getCurrentAuthenticatedUserContext();
  }, [fetchCurrentUser, fetchEmployee]);

  useEffect(() => {
    async function getCurrentNonAuthenticatedUserContext() {
      const token = getAccessToken({ withUrl: true });

      if (token && loginlessPages?.includes(location.pathname)) {
        setCurrentUser({
          isAuthenticated: false,
          token,
        });

        const searchParams = new URLSearchParams(location.search);
        const userId = searchParams.get('user');

        await fetchCurrentUser({
          variables: {
            userId,
          },
        });
      }
    }

    getCurrentNonAuthenticatedUserContext();
  }, [fetchCurrentUser, location.pathname, location.search]);

  useEffect(() => {
    const authToken = getAccessToken({ withLocalStorage: true });
    if (currentUserQuery?.currentAuthUser?.organization) {
      setCurrentUser(defineUserQueryData(currentUserQuery?.currentAuthUser, !!authToken));
    }
  }, [currentUserQuery?.currentAuthUser]);

  useEffect(() => {
    if (employeeQuery?.employee) {
      setUserEmployeeData(employeeQuery.employee);
    }
  }, [employeeQuery?.employee]);

  useEffect(() => {
    if (currentUserQueryError || employeeQueryError) {
      client.clearStore();
      setCurrentUser(currentUserInitialState);
      LocalStorage.clear();
      navigate('/signin', { replace: true });
    }
  }, [client, currentUserQueryError, employeeQueryError, navigate]);

  useEffect(() => {
    if (!fetchCurrentUserLoading && !employeeQueryLoading) {
      setLoading(false);
    }
  }, [employeeQueryLoading, fetchCurrentUserLoading]);

  if (fetchCurrentUserLoading || employeeQueryLoading || loading) {
    return <Loader />;
  }

  const onCacheClear = async () => {
    await client.resetStore();
  };

  return (
    <>
      <CacheBuster
        currentVersion={packageJSON.version}
        isEnabled={isProduction} //If false, the library is disabled.
        isVerboseMode={true} //If true, the library writes verbose logs to console.
        loadingComponent={<Loader />} //If not pass, nothing appears at the time of new version check.
        onCacheClear={onCacheClear}
      >
        <Routes />
      </CacheBuster>
    </>
  );
};

export default App;
